import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import SeatMap from "@/components/SeatMap";
import { Radio, Film, Clock, MapPin, Users, ShoppingBag, Sparkles, Ticket, CheckCircle2, Printer, Popcorn, CreditCard, Smartphone } from "lucide-react";

/**
 * POSCustomerDisplay — 2. Bildschirm / Kunden-Display, verbunden per WebSocket.
 * Modi:
 *   - idle:       Logo + rotierendes Poster/Trailer-Karussell
 *   - active:     Sitzplan + Live-Warenkorb (Film-Verkauf)
 *   - snacks:     Große Snack-Ansicht (nur Getränke/Popcorn, keine Vorstellung)
 *   - stripe_qr:  Vollbild-QR für Kartenzahlung
 *   - thanks:     Danke-Screen mit Poster + Countdown
 */
export default function POSCustomerDisplay() {
  const { displayKey } = useParams();
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [peers, setPeers] = useState(0);
  const [tick, setTick] = useState(0);
  const wsRef = useRef(null);

  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = base.replace(/^https/i, "wss").replace(/^http/i, "ws") + `/api/pos/display/${displayKey}`;
    let ws, reconnect, hb, closed = false;
    const connect = () => {
      setStatus("connecting");
      try { ws = new WebSocket(url); } catch { setStatus("error"); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("live");
        hb = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data);
          if (ev.type === "state") setState(ev.state);
          else if (ev.type === "close") setState(null);
          else if (ev.type === "hello" || ev.type === "peers") setPeers(ev.peers || 0);
        } catch { /* noop */ }
      };
      ws.onclose = () => {
        setStatus("closed");
        if (hb) clearInterval(hb);
        if (!closed) reconnect = setTimeout(connect, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      closed = true;
      if (hb) clearInterval(hb);
      if (reconnect) clearTimeout(reconnect);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
  }, [displayKey]);

  const now = useMemo(() => new Date(), [tick]);
  const clock = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

  // Modus-Weichen
  if (state?.mode === "stripe_qr") {
    return <StripeQrScreen state={state} clock={clock} status={status} peers={peers} />;
  }
  if (state?.mode === "thanks") {
    return <ThanksScreen state={state} clock={clock} now={now} status={status} peers={peers} />;
  }

  const hasActive = state && (state.showtime || (state.selected || []).length || (state.products || []).length);
  if (!hasActive) {
    return <IdleScreen status={status} peers={peers} clock={clock} />;
  }

  return <ActiveScreen state={state} clock={clock} status={status} />;
}

/* -------------------- Aktiver Verkauf (Sitze + Snacks) ------------------ */
function ActiveScreen({ state, clock, status }) {
  const s = state;
  const showtime = s.showtime;
  const selected = s.selected || [];
  const products = s.products || [];
  const priceEach = Number(s.priceEach || 0);
  const seatSum = priceEach * selected.length;
  const productSum = products.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.unit_price || 0)), 0);
  const subtotal = seatSum + productSum;
  const tierDiscount = Number(s.tierDiscount || 0);
  const discount = Number(s.discount || 0);
  const voucherDeducted = Number(s.voucherDeducted || 0);
  const total = Number(s.total != null ? s.total : Math.max(0, subtotal - discount - tierDiscount - voucherDeducted));

  // Snack-Only: keine Vorstellung, aber Produkte im Warenkorb → dedizierter Snack-Screen
  const snackOnly = !showtime && products.length > 0;
  if (snackOnly) {
    return <SnackScreen state={state} clock={clock} status={status} total={total} subtotal={subtotal} tierDiscount={tierDiscount} discount={discount} voucherDeducted={voucherDeducted} />;
  }

  return (
    <div className="min-h-screen pos-display-bg text-[#F5F0E6]" data-testid="pos-customer-display">
      <DisplayHeader clock={clock} status={status} title="Ihre Vorstellung" />
      <div className="grid grid-cols-[380px_1fr_380px] gap-6 px-8 py-6">
        <aside className="space-y-5">
          {showtime?.movie?.poster_url && (
            <div className="rounded-xl overflow-hidden border border-[#D4AF37]/30 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
              <img src={showtime.movie.poster_url} alt="" className="w-full block" />
            </div>
          )}
          {showtime && (
            <div className="card-lux p-5 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2">
                <Film size={14} /> Vorstellung
              </div>
              <div className="font-heading text-3xl leading-tight">{showtime.movie?.title_de || showtime.movie?.title_original}</div>
              {showtime.movie?.title_original && showtime.movie?.title_original !== showtime.movie?.title_de && (
                <div className="text-sm text-[#A8A398] italic">{showtime.movie.title_original}</div>
              )}
              <div className="pt-3 border-t border-[#1A1A1A] space-y-2 text-base">
                <Row icon={<Clock size={16} />} label="Datum & Zeit" value={`${formatDateEU(showtime.date)} · ${showtime.start_time}`} />
                <Row icon={<MapPin size={16} />} label="Saal" value={showtime.hall} />
                {showtime.movie?.duration_min && (
                  <Row icon={<Clock size={16} />} label="Dauer" value={`${showtime.movie.duration_min} min`} />
                )}
                {showtime.movie?.age_rating && (
                  <Row icon={<Users size={16} />} label="FSK" value={showtime.movie.age_rating} />
                )}
              </div>
            </div>
          )}
        </aside>

        <div className="card-lux p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Sitzplatzauswahl</div>
              <div className="font-heading text-2xl">Wählen Sie Ihren Platz</div>
            </div>
            {selected.length > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Ausgewählt</div>
                <div className="font-heading text-3xl text-[#EEDC82]">{selected.length}</div>
              </div>
            )}
          </div>
          {showtime?.seat_plan?.rows?.length ? (
            <div className="seatmap-display flex-1 overflow-auto">
              <SeatMap
                rows={showtime.seat_plan?.rows || []}
                seatTypes={showtime.seat_plan?.seat_types || []}
                seatStatus={showtime.seat_status || {}}
                selected={selected}
                readOnly
                curve={6}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#575148] text-lg">
              Kein Sitzplan verfügbar
            </div>
          )}
        </div>

        <OrderSummary
          selected={selected}
          ticketType={s.ticketType}
          priceEach={priceEach}
          products={products}
          subtotal={subtotal}
          tierDiscount={tierDiscount}
          discount={discount}
          voucherDeducted={voucherDeducted}
          total={total}
          payment={s.payment}
          guest={s.guest}
          tierInfo={s.tierInfo}
        />
      </div>

      <DisplayFooter />
    </div>
  );
}

/* -------------------- Nur Snacks / Getränke ----------------------------- */
function SnackScreen({ state, clock, status, total, subtotal, tierDiscount, discount, voucherDeducted }) {
  const products = state.products || [];
  const totalItems = products.reduce((s, p) => s + Number(p.quantity || 0), 0);
  return (
    <div className="min-h-screen pos-display-bg text-[#F5F0E6]" data-testid="pos-customer-display-snacks">
      <DisplayHeader clock={clock} status={status} title="Snackbar" subtitle="Ihre Auswahl" />
      <div className="grid grid-cols-[1fr_400px] gap-6 px-8 py-8">
        <div className="card-lux p-8 flex flex-col items-center justify-center text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 70%, #EE9CA1 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
          <Popcorn size={92} className="text-[#D4AF37] mb-6 relative" strokeWidth={1.2} />
          <div className="text-[10px] uppercase tracking-[0.55em] text-[#D4AF37] relative">Cinema · Bar</div>
          <div className="font-heading text-7xl mt-4 mb-3 relative">Snacks & Getränke</div>
          <div className="text-xl text-[#A8A398] max-w-xl relative">
            Perfekte Begleiter für Ihren Kinoabend. Alle Artikel Ihrer aktuellen
            Bestellung erscheinen live auf der rechten Seite.
          </div>
          {products.length > 0 && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl w-full relative">
              {products.slice(0, 6).map((p) => (
                <div key={p.product_id} className="border border-[#D4AF37]/25 bg-[#0F0D08]/60 rounded-xl p-4 text-left">
                  <div className="text-[9px] uppercase tracking-[0.35em] text-[#D4AF37]">Aktuell</div>
                  <div className="font-heading text-lg mt-1 leading-tight">{p.name}</div>
                  <div className="text-[#A8A398] text-sm mt-1 font-mono">
                    {p.quantity}× · {(Number(p.quantity) * Number(p.unit_price)).toFixed(2)} €
                  </div>
                </div>
              ))}
            </div>
          )}
          {totalItems > 0 && (
            <div className="mt-8 inline-flex items-center gap-3 border border-[#D4AF37]/50 rounded-full px-5 py-2 text-sm uppercase tracking-widest">
              <ShoppingBag size={14} className="text-[#D4AF37]" />
              <span className="text-[#EEDC82]">{totalItems} Artikel</span>
              <span className="text-[#575148]">·</span>
              <span className="font-mono text-[#F5F0E6]">{subtotal.toFixed(2)} €</span>
            </div>
          )}
        </div>

        <OrderSummary
          selected={[]}
          products={products}
          subtotal={subtotal}
          tierDiscount={tierDiscount}
          discount={discount}
          voucherDeducted={voucherDeducted}
          total={total}
          payment={state.payment}
          guest={state.guest}
          tierInfo={state.tierInfo}
          hideEmpty
        />
      </div>
      <DisplayFooter />
    </div>
  );
}

/* -------------------- Stripe-QR Vollbild -------------------------------- */
function StripeQrScreen({ state, clock, status, peers }) {
  const amt = Number(state.amount || 0);
  const showtime = state.showtime;
  return (
    <div className="min-h-screen pos-display-bg text-[#F5F0E6]" data-testid="pos-customer-display-stripe">
      <DisplayHeader clock={clock} status={status} title="Kartenzahlung" subtitle="Bitte mit dem Handy scannen" peers={peers} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 items-center px-14 py-6">
        <div className="text-center lg:text-left space-y-6 animate-in fade-in slide-in-from-left-6 duration-700">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] rounded-full px-4 py-1 text-[10px] uppercase tracking-[0.5em]">
            <CreditCard size={12} /> Sichere Zahlung · Stripe
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.45em] text-[#A8A398] mb-2">Zu zahlender Betrag</div>
            <div className="font-heading text-8xl text-[#D4AF37] leading-none tabular-nums">{amt.toFixed(2)} €</div>
          </div>
          {showtime?.movie && (
            <div className="card-lux p-5 max-w-md mx-auto lg:mx-0">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Ihre Vorstellung</div>
              <div className="font-heading text-2xl mt-1">{showtime.movie.title_de || showtime.movie.title_original}</div>
              <div className="text-sm text-[#A8A398] mt-1">
                {formatDateEU(showtime.date)} · {showtime.start_time} · {showtime.hall}
              </div>
            </div>
          )}
          <div className="text-lg text-[#A8A398] max-w-md">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-heading">1</div>
              <span>Handy-Kamera öffnen</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-heading">2</div>
              <span>QR-Code scannen</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-heading">3</div>
              <span>Bezahlen — fertig</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center lg:justify-end animate-in fade-in zoom-in-95 duration-700">
          <div className="relative">
            <div className="bg-white p-8 rounded-3xl shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
              {state.stripeUrl ? (
                <QRCodeCanvas value={state.stripeUrl} size={420} includeMargin={false} level="M" />
              ) : (
                <div className="w-[420px] h-[420px] flex items-center justify-center text-[#575148]">
                  <Smartphone size={80} />
                </div>
              )}
            </div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.5em] px-5 py-1.5 rounded-full font-heading">
              QR-Code · Stripe
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-[#1A1A1A] px-10 py-3 text-center text-[10px] uppercase tracking-[0.5em] text-[#575148]">
        Ihre Zahlung wird verschlüsselt über Stripe verarbeitet · THE MOVIE HOUSE
      </div>
    </div>
  );
}

/* -------------------- Danke-Screen ------------------------------------- */
function ThanksScreen({ state, clock, now, status, peers }) {
  const st = state.showtime;
  const startAt = useMemo(() => {
    if (!st?.date || !st?.start_time) return null;
    try { return new Date(`${st.date}T${st.start_time}:00`); } catch { return null; }
  }, [st?.date, st?.start_time]);
  const diffSec = startAt ? Math.max(0, Math.floor((startAt.getTime() - now.getTime()) / 1000)) : 0;
  const h = Math.floor(diffSec / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const sec = diffSec % 60;
  const started = startAt && startAt.getTime() <= now.getTime();

  return (
    <div className="min-h-screen pos-display-bg text-[#F5F0E6] flex flex-col" data-testid="pos-customer-display-thanks">
      <DisplayHeader clock={clock} status={status} title="Danke für Ihren Kauf" peers={peers} />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-10 items-center px-10 py-8">
        {st?.movie?.poster_url ? (
          <div className="rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(0,0,0,0.8)] animate-in fade-in zoom-in duration-700">
            <img src={st.movie.poster_url} alt={st.movie.title_de} className="w-full block" />
          </div>
        ) : (
          <div className="aspect-[2/3] rounded-2xl bg-gradient-to-br from-[#8B7328]/30 to-[#0A0A0A] border border-[#D4AF37]/30 flex items-center justify-center">
            <Film size={80} className="text-[#D4AF37]/40" />
          </div>
        )}

        <div className="space-y-8 text-center lg:text-left animate-in fade-in slide-in-from-right-6 duration-700">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] rounded-full px-4 py-1 text-[10px] uppercase tracking-[0.5em]">
              <CheckCircle2 size={12} /> Bestellung bestätigt
            </div>
            <h1 className="font-heading text-6xl lg:text-7xl mt-4 leading-tight">Danke!</h1>
            <div className="mt-3 text-3xl text-[#EEDC82] flex items-center gap-3 justify-center lg:justify-start">
              <Printer size={26} className="text-[#D4AF37]" />
              Ihre Tickets werden gedruckt
            </div>
          </div>

          {st && (
            <div className="card-lux p-6 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Ihre Vorstellung</div>
              <div className="font-heading text-4xl leading-tight">{st.movie?.title_de || st.movie?.title_original}</div>
              <div className="text-xl text-[#A8A398]">
                {formatDateEU(st.date)} · {st.start_time} · {st.hall}
                {st.movie?.duration_min ? ` · ${st.movie.duration_min} min` : ""}
              </div>
            </div>
          )}

          {startAt && (
            <div className="card-lux p-6 border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/10 to-transparent">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">
                {started ? "Vorstellung hat begonnen — viel Spaß!" : "Vorstellung beginnt in"}
              </div>
              {!started ? (
                <div className="flex items-center gap-4 justify-center lg:justify-start font-mono text-[#EEDC82]">
                  {h > 0 && <TimeBox v={h} label="Std" />}
                  <TimeBox v={m} label="Min" />
                  <TimeBox v={sec} label="Sek" />
                </div>
              ) : (
                <div className="text-3xl text-[#EEDC82] font-heading">Genießen Sie den Film</div>
              )}
            </div>
          )}

          <div className="flex items-center justify-center lg:justify-start gap-6 text-lg text-[#A8A398] flex-wrap">
            {state.ticketsCount > 0 && (
              <div className="inline-flex items-center gap-2">
                <Ticket size={20} className="text-[#D4AF37]" />
                {state.ticketsCount} Ticket{state.ticketsCount === 1 ? "" : "s"}
              </div>
            )}
            {state.total > 0 && (
              <div className="inline-flex items-center gap-2 font-mono">
                <span className="text-[#575148]">Gesamt:</span>
                <span className="text-[#D4AF37]">{Number(state.total).toFixed(2)} €</span>
              </div>
            )}
            {state.orderNumber && (
              <div className="text-xs uppercase tracking-widest text-[#575148]">Nr. {state.orderNumber}</div>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-[#1A1A1A] px-8 py-3 text-center text-[10px] uppercase tracking-[0.4em] text-[#575148]">
        Bitte nehmen Sie Ihre Tickets an der Kasse entgegen · THE MOVIE HOUSE
      </div>
    </div>
  );
}

/* -------------------- Idle mit Poster + Trailer ------------------------- */
function IdleScreen({ status, peers, clock }) {
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [progRes, previewRes] = await Promise.all([
          api.get("/public/program").catch(() => ({ data: [] })),
          api.get("/public/preview-movies").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const seen = new Set();
        const uniq = [];
        (progRes.data || []).forEach((s) => {
          const m = s.movie;
          if (!m?.poster_url) return;
          const key = m.id || m.title_de || m.title_original;
          if (!key || seen.has(key)) return;
          seen.add(key); uniq.push(m);
        });
        (previewRes.data || []).forEach((m) => {
          if (!m?.poster_url) return;
          const key = m.id || m.title_de || m.title_original;
          if (!key || seen.has(key)) return;
          seen.add(key); uniq.push(m);
        });
        setRows(uniq);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 120_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Rotation alle 12 s (länger, damit Trailer Zeit hat zum Anspielen)
  useEffect(() => {
    if (rows.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % rows.length), 12000);
    return () => clearInterval(t);
  }, [rows.length]);

  const current = rows[idx];
  const preview = rows.slice(0, 6);
  const trailerId = current ? getYoutubeId(current.trailer_url) : "";

  return (
    <div className="min-h-screen pos-display-bg text-[#F5F0E6] flex flex-col overflow-hidden" data-testid="pos-customer-display-idle">
      <div className="border-b border-[#1A1A1A] px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/25 to-transparent">
            <Sparkles size={22} className="text-[#D4AF37]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.55em] text-[#D4AF37]">The Movie House</div>
            <div className="font-heading text-xl leading-tight">Cinema · Kinokasse</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-4xl font-mono text-[#EEDC82] leading-none">{clock}</div>
          <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest ${status === "live" ? "border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#8B7328]/40 text-[#EEDC82]"}`}>
            <Radio size={12} className={status === "live" ? "animate-pulse" : ""} />
            {status === "live" ? `Live · ${peers}` : "Verbinde …"}
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1fr] items-center gap-10 px-14 py-8">
        <div className="text-center lg:text-left space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.6em] text-[#D4AF37]">
              <Sparkles size={12} /> Willkommen
            </div>
            <h1 className="font-heading text-6xl lg:text-7xl mt-3 leading-none tracking-tight">
              THE MOVIE<br />HOUSE
            </h1>
            <div className="text-xl text-[#A8A398] italic mt-2">More than watching movies.</div>
          </div>
          {/* Trailer-Preview (muted, autoplay) */}
          {trailerId ? (
            <div className="rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-[0_25px_60px_rgba(0,0,0,0.7)] aspect-video max-w-[560px] mx-auto lg:mx-0 bg-black">
              <iframe
                key={trailerId}
                src={`https://www.youtube-nocookie.com/embed/${trailerId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&fs=0&iv_load_policy=3&playsinline=1&loop=1&playlist=${trailerId}&showinfo=0`}
                title={current?.title_de || "trailer"}
                allow="autoplay; encrypted-media"
                frameBorder="0"
                className="w-full h-full"
                data-testid="idle-trailer-iframe"
              />
            </div>
          ) : (
            <div className="max-w-lg mx-auto lg:mx-0 text-lg text-[#A8A398] leading-relaxed">
              Bitte wenden Sie sich an das Kassenpersonal — Ihr Sitzplan und Ihre
              Auswahl erscheinen gleich hier auf diesem Bildschirm.
            </div>
          )}
          {preview.length > 1 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148] mb-2">Diese Woche im Programm</div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {preview.map((m, i) => (
                  <div key={m.id || m.title_de || i}
                    className={`shrink-0 w-20 rounded-md overflow-hidden border transitions-safe cursor-default ${i === idx ? "border-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.35)]" : "border-[#1A1A1A] opacity-70"}`}>
                    <img src={m.poster_url} alt="" className="w-full block" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center lg:justify-end">
          {current ? (
            <div key={`p-${idx}`}
              className="relative w-[min(65vh,460px)] aspect-[2/3] rounded-3xl overflow-hidden border border-[#D4AF37]/50 shadow-[0_40px_100px_rgba(0,0,0,0.8)] animate-in fade-in zoom-in-95 duration-1000">
              <img src={current.poster_url} alt={current.title_de} className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-6">
                <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Bald bei uns</div>
                <div className="font-heading text-3xl leading-tight mt-1">{current.title_de || current.title_original}</div>
                {current.duration_min && (
                  <div className="text-sm text-[#A8A398] mt-1">
                    {current.duration_min} min
                    {current.age_rating ? ` · ${String(current.age_rating).toUpperCase().startsWith("FSK") ? "" : "FSK "}${current.age_rating}` : ""}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="w-[min(60vh,420px)] aspect-[2/3] rounded-3xl bg-gradient-to-br from-[#8B7328]/25 via-[#0A0A0A] to-[#0A0A0A] border border-[#D4AF37]/30 flex flex-col items-center justify-center gap-4">
              <Sparkles size={80} className="text-[#D4AF37]/60 animate-pulse" />
              <div className="text-xl text-[#A8A398] uppercase tracking-widest">Kino · Genuss · Emotion</div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-[#1A1A1A] px-10 py-3 flex items-center justify-between text-[10px] uppercase tracking-[0.5em] text-[#575148]">
        <div>Herzlich willkommen im The Movie House</div>
        <div>Cinema OS</div>
      </div>
    </div>
  );
}

/* -------------------- gemeinsame Bausteine ------------------------------ */
function DisplayHeader({ clock, status, title, subtitle, peers }) {
  return (
    <div className="border-b border-[#1A1A1A] px-8 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Sparkles size={22} className="text-[#D4AF37]" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">The Movie House</div>
          <div className="font-heading text-2xl leading-tight">{title}</div>
          {subtitle && <div className="text-xs text-[#A8A398] mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="text-4xl font-mono text-[#EEDC82] leading-none">{clock}</div>
        <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest ${status === "live" ? "border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}>
          <Radio size={12} className={status === "live" ? "animate-pulse" : ""} />
          {status === "live" ? (peers != null ? `Live · ${peers}` : "Live") : "Verbinde …"}
        </div>
      </div>
    </div>
  );
}

function DisplayFooter() {
  return (
    <div className="border-t border-[#1A1A1A] px-8 py-3 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] text-[#575148]">
      <div>Danke für Ihren Besuch — genießen Sie den Film!</div>
      <div>THE MOVIE HOUSE · Kinokasse</div>
    </div>
  );
}

function OrderSummary({ selected = [], ticketType, priceEach = 0, products = [], subtotal, tierDiscount, discount, voucherDeducted, total, payment, guest, tierInfo, hideEmpty }) {
  const empty = !selected.length && !products.length;
  if (hideEmpty && empty) return <div />;
  return (
    <aside className="space-y-4">
      <div className="card-lux p-5">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2 mb-3">
          <ShoppingBag size={14} /> Ihre Bestellung
        </div>
        <div className="max-h-[54vh] overflow-y-auto space-y-1">
          {selected.map((seatId) => (
            <div key={seatId} className="flex justify-between items-baseline border-b border-[#1A1A1A]/60 py-2">
              <div>
                <div className="text-lg">Ticket · {ticketType || "Standard"}</div>
                <div className="text-xs text-[#A8A398] font-mono">Platz {seatId}</div>
              </div>
              <div className="font-mono text-lg text-[#F5F0E6]">{priceEach.toFixed(2)} €</div>
            </div>
          ))}
          {products.map((p) => (
            <div key={p.product_id} className="flex justify-between items-baseline border-b border-[#1A1A1A]/60 py-2">
              <div>
                <div className="text-lg">{p.name}</div>
                <div className="text-xs text-[#A8A398] font-mono">{p.quantity}× {Number(p.unit_price).toFixed(2)} €</div>
              </div>
              <div className="font-mono text-lg text-[#F5F0E6]">{(p.quantity * p.unit_price).toFixed(2)} €</div>
            </div>
          ))}
          {empty && <div className="text-[#575148] text-center py-10 text-lg">Bitte wählen Sie …</div>}
        </div>
        {subtotal > 0 && (
          <div className="mt-4 pt-4 border-t border-[#1A1A1A] space-y-2 text-base">
            <Line label="Zwischensumme" value={`${subtotal.toFixed(2)} €`} />
            {tierDiscount > 0 && <Line label="Club-Rabatt" value={`− ${tierDiscount.toFixed(2)} €`} accent />}
            {discount > 0 && <Line label="Rabatt" value={`− ${discount.toFixed(2)} €`} accent />}
            {voucherDeducted > 0 && <Line label="Gutschein" value={`− ${voucherDeducted.toFixed(2)} €`} accent />}
            <div className="flex items-baseline justify-between pt-3 border-t border-[#1A1A1A]/60">
              <div className="font-heading text-2xl">Gesamt</div>
              <div className="font-heading text-4xl text-[#D4AF37]" data-testid="pos-display-total">{total.toFixed(2)} €</div>
            </div>
            {payment && (
              <div className="text-center text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mt-1">
                Zahlart: <span className="text-[#EEDC82]">{payLabel(payment)}</span>
              </div>
            )}
          </div>
        )}
      </div>
      {guest && guest !== "Kunde" && (
        <div className="card-lux p-4 text-center">
          <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Kunde</div>
          <div className="font-heading text-xl mt-1">{guest}</div>
          {tierInfo?.is_member && tierInfo.tier && (
            <div className="mt-1 inline-block px-3 py-0.5 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] uppercase tracking-widest">
              {tierInfo.tier} · Club
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[#D4AF37]">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#575148]">{label}</div>
        <div className="text-base">{value || "—"}</div>
      </div>
    </div>
  );
}

function Line({ label, value, accent }) {
  return (
    <div className={`flex justify-between items-baseline ${accent ? "text-[#D4AF37]" : "text-[#F5F0E6]"}`}>
      <span className="text-[#A8A398]">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function TimeBox({ v, label }) {
  return (
    <div className="text-center">
      <div className="font-heading text-6xl leading-none tabular-nums">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398] mt-1">{label}</div>
    </div>
  );
}

function formatDateEU(isoDate) {
  if (!isoDate) return "";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function payLabel(v) {
  const map = { bar: "Bar", karte: "Karte", karte_stripe: "Karte · Stripe", gutschein: "Gutschein", einladung: "Einladung", frei: "Kostenlos", mitglied: "Mitgliedskarte" };
  return map[v] || v;
}

function getYoutubeId(url) {
  if (!url) return "";
  try {
    if (url.includes("watch?v=")) return url.split("watch?v=")[1].split("&")[0];
    if (url.includes("youtu.be/")) return url.split("youtu.be/")[1].split(/[?&]/)[0];
    if (url.includes("embed/")) return url.split("embed/")[1].split(/[?&]/)[0];
  } catch { /* noop */ }
  return "";
}
